"use client";
import * as React from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import { FolderKanban, Plus, FolderOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../Common/PageHeader";
import { WorkspaceCard } from "../Common/WorkspaceCard";
import { EmptyState } from "../Common/EmptyState";
import { CardSkeletonList } from "../Common/CardSkeleton";
import { Row } from "../Common/Row";
import { CreateProjectDialog } from "./CreateProjectDialog";
import { listProjects, type ProjectSummary } from "../../api/projects";

function relativeDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function ProjectsListPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = React.useState<ProjectSummary[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const refresh = React.useCallback(() => {
    setError(null);
    listProjects()
      .then(setProjects)
      .catch((e) => {
        const msg = e instanceof Error ? e.message : String(e);
        // A 404 means the Projects feature isn't enabled on the backend
        // (UNIFIED_AGENT_PROJECTS_ENABLED). Show a calm "unavailable" empty
        // state rather than a red error — there's nothing the user can fix.
        if (/404/.test(msg)) {
          setError("unavailable");
        } else {
          setError(msg);
        }
        setProjects([]);
      });
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <Box>
      <PageHeader
        title="Projects"
        subtitle="Group related chats with shared instructions and files. Artie follows the project's instructions and can draw on its files in every chat you start there."
        actions={
          <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setDialogOpen(true)}>
            New project
          </Button>
        }
      />

      {error && error !== "unavailable" && (
        <Alert severity="error" sx={{ mb: 2 }}>Failed to load: {error}</Alert>
      )}

      {error === "unavailable" ? (
        <EmptyState
          icon={FolderKanban}
          title="Projects aren’t enabled yet"
          description="Projects are turned off for this workspace. An administrator can enable them to group chats with shared instructions and files."
        />
      ) : projects === null ? (
        <CardSkeletonList />
      ) : projects.length === 0 ? (
        <EmptyState
          icon={FolderKanban}
          title="No projects yet"
          description="Create a project to give Artie instructions and a set of files it can use across related chats."
          action={
            <Button size="small" startIcon={<Plus size={14} />} onClick={() => setDialogOpen(true)}>
              New project
            </Button>
          }
        />
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
            gap: 2,
          }}
        >
          {projects.map((p) => (
            <WorkspaceCard
              key={p.id}
              onClick={() => navigate(`/workspace/projects/${p.id}`)}
              sx={{ cursor: "pointer", display: "flex", flexDirection: "column", gap: 1, minHeight: 110 }}
            >
              <Row align="center" gap={1.25}>
                <Box
                  aria-hidden
                  sx={{
                    display: "grid",
                    placeItems: "center",
                    width: 36,
                    height: 36,
                    flexShrink: 0,
                    borderRadius: 2,
                    color: "primary.contrastText",
                    bgcolor: "primary.main",
                  }}
                >
                  <FolderOpen size={18} />
                </Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {p.name}
                </Typography>
              </Row>
              <Typography variant="caption" color="text.secondary" sx={{ mt: "auto" }}>
                Updated {relativeDate(p.updated_at)}
              </Typography>
            </WorkspaceCard>
          ))}
        </Box>
      )}

      <CreateProjectDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={(project) => {
          setDialogOpen(false);
          navigate(`/workspace/projects/${project.id}`);
        }}
      />
    </Box>
  );
}
